<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Użytkownicy</span>
        <h3 class="page-title">Lista produktów</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          class="text-nowrap"
          @click.prevent="$router.push('/produkty/nowy')">
          <i class="material-icons">add</i>
          Dodaj produkt
        </d-button>
      </d-col>
    </d-row>

    <d-row
      class="d-flex ml-0 mb-3 page-filters w-100">
      <validation-observer
        ref="products-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę produktu"
          class="mr-2"
          v-model="filters.search" />
        <div :class="{ 'form-control': true, 'is-select': true }">
          <v-select
            :options="categories"
            :reduce="category => category.id"
            v-model="filters.category_id"
            :loading="!categories_loaded && !categories_load_error"
            placeholder="Kategoria produktu"
            :disabled="!categories_loaded && !categories_load_error"
            :searchable="false"
            label="name">
            <template
              slot="selected-option"
              slot-scope="option">
              <div class="selected d-center">
                {{ option.name ? option.name : (option.id ? 'Kategoria #' + option.id : '') }}
              </div>
            </template>
            <template slot="no-options">
              <div
                v-if="categories_load_error"
                class="text-center py-2">
                <div class="text-danger mb-1">Błąd ładowania kategorii</div>
                <a
                  href="#"
                  @click.prevent="getCategories">Ponów</a>
              </div>
              <div v-else>
                Brak wyników
              </div>
            </template>
          </v-select>
        </div>
        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          :disabled="!loaded"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          :disabled="!loaded"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0"
              width="150px">Kolejność</th>
            <th
              scope="col"
              class="border-0">Nazwa</th>
            <th
              scope="col"
              class="border-0">Kategoria</th>
            <th
              scope="col"
              class="border-0">Grupa</th>
            <th
              scope="col"
              class="border-0">Cena</th>
            <th
              scope="col"
              class="border-0">Vat</th>
            <th
              scope="col"
              class="border-0 text-center"
              width="140">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded && !load_error && products">
          <tr
            v-for="(product, index) of products"
            :key="'user-' + index">
            <td class="align-middle">
              {{ product.ordering }}
            </td>
            <td class="align-middle">
              {{ product.name }}
            </td>
            <td class="align-middle">
              <template v-if="product.category_id">
                {{ product.category_name }}
              </template>
              <span
                v-else
                style="opacity: 0.3">
                Brak
              </span>
            </td>
            <td class="align-middle">
              <template v-if="product.product_group_id">
                {{ product.product_group_name }}
              </template>
              <span
                v-else
                style="opacity: 0.3">
                Brak
              </span>
            </td>
            <td class="align-middle">
              {{ product.price }}
            </td>
            <td class="align-middle">
              {{ product.vat_rate }} %
            </td>
            <td
              class="align-middle text-center d-flex justify-content-center"
              style="gap: 12px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/produkty/edycja/' + product.id)"
                class="mr-2 action-button"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                class="action-button"
                theme="danger"
                @click.native="removeProduct(product.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="products.length === 0">
            <td
              colspan="7"
              class="text-center">
              Brak użytkowników do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <d-alert
        v-if="load_error"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a
          href="javascript:window.location.reload();"
        class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>

      <div
        v-else-if="!loaded"
        class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>
    </d-card>
    <pagination
      v-if="loaded && !load_error"
      :meta-data="paginationMetaData"
      :initial-page="currentPage"/>

  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'products',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data () {
    return {
      products: [],
      categories: [],
      categories_loaded: false,
      categories_load_error: false,
      loaded: false,
      load_error: false,
      removeInProgress: false,
      filters: {
        search: '',
        category_id: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.getCategories();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };
      if (this.filters.category_id !== null && this.filters.category_id) {
        where.category_id = this.filters.category_id;
      }
      ListUtils.loadItemsData(this, {
        endpoint: '/products/items?page=' + this.currentPage,
        listField: 'products',
        errorKey: 'load_error',
        pagination: true,
        params: {
          where,
          order: {
            column: 'ordering',
            direction: 'ASC'
          }
        }
      });
    },
    getCategories () {
      this.categories_loaded = false;
      this.categories_load_error = false;
      ListUtils.loadItemsData(this, {
        endpoint: '/categories/items',
        listField: 'categories',
        loadedKey: 'categories_loaded',
        errorKey: 'categories_load_error',
        pagination: false,
      });
    },
    clearFilters () {
      this.loaded = false;
      this.load_error = false;
      this.filters = {
        search: '',
        category_id: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['products-filters-form'], this.debouncedLoadFilteredData);
    },
    removeProduct (productsID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/products/delete/',
        id: productsID,
        successTitle: 'Usunięto produkt',
        successText: 'Wybrany produkt został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć ten produkt?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie produktu nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

